"use client"

import {
  AlertCircle,
  CheckCircle,
  Clock,
  Settings01,
  Zap,
} from "@untitledui/icons"
import { format } from "date-fns"
import { useState } from "react"

import { StatusBadge, type StatusTier } from "@/components/ui/badge"
import { EmptyState } from "@/components/ui/empty-state"
import { TabList, Tabs } from "@/components/ui/tabs"
import { cn } from "@/lib/utils"

export interface InspectorOutcome {
  id: number
  key: string
  value: unknown
  recorded_at: string | null
}

export interface InspectorFeedLine {
  id: string
  kind: "observation" | "intervention" | "ops_alert"
  subkind: string | null
  step_code: string | null
  at: string
  severity: string | null
  actor: string | null
  body: string | null
  description: string
}

export interface InspectorTick {
  id: number
  kind: string
  step_code: string | null
  fires_at: string | null
  executed_at: string | null
  cancelled_at: string | null
  status: string | null
}

export interface InspectorData {
  outcomes: InspectorOutcome[]
  observations: InspectorFeedLine[]
  interventions: InspectorFeedLine[]
  ticks: InspectorTick[]
}

const TABS = [
  { id: "outcomes", label: "Outcomes" },
  { id: "observations", label: "Observations" },
  { id: "interventions", label: "Interventions" },
  { id: "ticks", label: "Ticks" },
] as const

type TabId = (typeof TABS)[number]["id"]

const formatTime = (iso: string | null) =>
  iso ? format(new Date(iso), "d MMM · HH:mm") : "—"

export function RunInspector({ inspector }: { inspector: InspectorData }) {
  const [activeTab, setActiveTab] = useState<TabId>("outcomes")
  const counts = {
    outcomes: inspector.outcomes.length,
    observations: inspector.observations.length,
    interventions: inspector.interventions.length,
    ticks: inspector.ticks.length,
  }
  const tabs = TABS.map((t) => ({
    ...t,
    badge: counts[t.id] > 0 ? counts[t.id] : undefined,
  }))

  return (
    <div className="flex flex-col gap-5">
      <header className="flex flex-col gap-1">
        <h2 className="text-primary text-md font-semibold">Inspector</h2>
        <p className="text-tertiary text-xs">
          Audit log derived from the engine's own state.
        </p>
      </header>
      <Tabs
        selectedKey={activeTab}
        onSelectionChange={(k) => setActiveTab(String(k) as TabId)}
      >
        <TabList size="sm" type="underline" items={tabs} />
      </Tabs>
      <div>
        {activeTab === "outcomes" && (
          <OutcomesPanel outcomes={inspector.outcomes} />
        )}
        {activeTab === "observations" && (
          <ObservationsPanel items={inspector.observations} />
        )}
        {activeTab === "interventions" && (
          <InterventionsPanel items={inspector.interventions} />
        )}
        {activeTab === "ticks" && <TicksPanel ticks={inspector.ticks} />}
      </div>
    </div>
  )
}

// ---- Outcomes ---------------------------------------------------------------

function OutcomesPanel({ outcomes }: { outcomes: InspectorOutcome[] }) {
  if (outcomes.length === 0) {
    return (
      <TabEmptyState
        title="No outcomes yet"
        description="Outcomes are recorded as the run progresses through its steps."
      />
    )
  }
  return (
    <dl className="border-secondary flex flex-col divide-y rounded-lg border">
      {outcomes.map((o) => (
        <div key={o.id} className="flex flex-col gap-1 px-4 py-3">
          <dt className="eyebrow">{o.key.replace(/_/g, " ")}</dt>
          <dd className="text-primary text-sm font-medium">
            {formatOutcomeValue(o.value)}
          </dd>
          {o.recorded_at && (
            <p className="text-quaternary font-mono text-xs tabular-nums">
              {formatTime(o.recorded_at)}
            </p>
          )}
        </div>
      ))}
    </dl>
  )
}

function formatOutcomeValue(v: unknown): string {
  if (v === null || v === undefined) return "—"
  if (typeof v === "string") return v
  if (typeof v === "number" || typeof v === "boolean") return String(v)
  return JSON.stringify(v)
}

// ---- Observations -----------------------------------------------------------

const SEVERITY_COLOR: Record<string, StatusTier> = {
  info: "neutral",
  notice: "remote",
  warning: "caution",
  alert: "caution",
}

function ObservationsPanel({ items }: { items: InspectorFeedLine[] }) {
  if (items.length === 0) {
    return (
      <TabEmptyState
        title="No observations"
        description="Signals like late-reply or low-score show up here when the engine detects them."
      />
    )
  }
  return (
    <FeedList>
      {items.map((line, i) => (
        <FeedRow
          key={line.id}
          icon={AlertCircle}
          iconColor="text-fg-warning-primary bg-warning-primary"
          connector={i < items.length - 1}
        >
          <div className="flex items-center gap-2">
            <p className="text-primary text-sm font-medium">
              {line.subkind?.replace(/_/g, " ") ?? "observation"}
            </p>
            {line.severity && (
              <StatusBadge tier={SEVERITY_COLOR[line.severity] ?? "neutral"}>
                {line.severity}
              </StatusBadge>
            )}
          </div>
          <p className="text-quaternary font-mono text-xs tabular-nums">
            {formatTime(line.at)}
          </p>
        </FeedRow>
      ))}
    </FeedList>
  )
}

// ---- Interventions (+ ops_alert) -------------------------------------------

const INTERVENTION_KIND_COLOR: Record<string, StatusTier> = {
  pause: "caution",
  resume: "remote",
  terminate: "system",
  quiet_hours_shift: "remote",
  fire_gate_deferred: "neutral",
}

function InterventionsPanel({ items }: { items: InspectorFeedLine[] }) {
  if (items.length === 0) {
    return (
      <TabEmptyState
        title="No interventions"
        description="Pauses, resumes, terminates, quiet-hours shifts, and ops alerts appear here."
      />
    )
  }
  return (
    <FeedList>
      {items.map((line, i) => {
        const isOps = line.kind === "ops_alert"
        return (
          <FeedRow
            key={line.id}
            icon={isOps ? Zap : Settings01}
            iconColor={
              isOps
                ? "text-fg-warning-primary bg-warning-primary"
                : "text-fg-brand-secondary bg-brand-section"
            }
            connector={i < items.length - 1}
          >
            <div className="flex items-center gap-2">
              <p className="text-primary text-sm font-medium">
                {(line.subkind ?? line.kind).replace(/_/g, " ")}
              </p>
              {!isOps && line.subkind && (
                <StatusBadge
                  tier={INTERVENTION_KIND_COLOR[line.subkind] ?? "neutral"}
                >
                  {line.actor ?? "system"}
                </StatusBadge>
              )}
              {isOps && <StatusBadge tier="caution">Ops alert</StatusBadge>}
            </div>
            {line.body && <p className="text-secondary text-sm">{line.body}</p>}
            <p className="text-quaternary font-mono text-xs tabular-nums">
              {formatTime(line.at)}
            </p>
          </FeedRow>
        )
      })}
    </FeedList>
  )
}

// ---- Ticks ------------------------------------------------------------------

const TICK_KIND_LABEL: Record<string, string> = {
  send_step: "Send",
  nudge: "Nudge",
  timeout_check: "Timeout check",
  after_send: "After send",
}

const TICK_STATUS_TIER: Record<
  "cancelled" | "executed" | "scheduled",
  StatusTier
> = {
  executed: "confirmed",
  cancelled: "system",
  scheduled: "remote",
}

function TicksPanel({ ticks }: { ticks: InspectorTick[] }) {
  if (ticks.length === 0) {
    return (
      <TabEmptyState
        title="No scheduled ticks"
        description="Each step's send / timeout / nudge ticks will appear here once scheduled."
      />
    )
  }
  return (
    <FeedList>
      {ticks.map((tick, i) => {
        const isCancelled = !!tick.cancelled_at
        const isExecuted = !!tick.executed_at
        const status: "cancelled" | "executed" | "scheduled" = isCancelled
          ? "cancelled"
          : isExecuted
            ? "executed"
            : "scheduled"
        return (
          <FeedRow
            key={tick.id}
            icon={
              status === "executed"
                ? CheckCircle
                : status === "cancelled"
                  ? AlertCircle
                  : Clock
            }
            iconColor={
              status === "executed"
                ? "text-fg-success-primary bg-success-primary"
                : status === "cancelled"
                  ? "text-fg-quaternary bg-quaternary"
                  : "text-fg-brand-secondary bg-brand-section"
            }
            connector={i < ticks.length - 1}
          >
            <div className="flex items-center gap-2">
              <p className="text-primary text-sm font-medium">
                {TICK_KIND_LABEL[tick.kind] ?? tick.kind}
                {tick.step_code && (
                  <>
                    {" · "}
                    <span className="font-mono text-xs tabular-nums">
                      {tick.step_code}
                    </span>
                  </>
                )}
              </p>
              <StatusBadge tier={TICK_STATUS_TIER[status]}>
                {status}
              </StatusBadge>
            </div>
            <p className="text-quaternary font-mono text-xs tabular-nums">
              {status === "executed"
                ? `Fired ${formatTime(tick.executed_at)}`
                : status === "cancelled"
                  ? `Cancelled ${formatTime(tick.cancelled_at)}`
                  : `Scheduled ${formatTime(tick.fires_at)}`}
            </p>
          </FeedRow>
        )
      })}
    </FeedList>
  )
}

// ---- Shared primitives ------------------------------------------------------

function FeedList({ children }: { children: React.ReactNode }) {
  return <ul className="flex flex-col">{children}</ul>
}

function FeedRow({
  icon: Icon,
  iconColor,
  connector,
  children,
}: {
  icon: React.ComponentType<{ className?: string }>
  iconColor: string
  connector: boolean
  children: React.ReactNode
}) {
  return (
    <li className="relative flex gap-3">
      <div className="flex shrink-0 flex-col items-center">
        <span
          className={cn(
            "ring-secondary flex size-8 items-center justify-center rounded-full ring-1 ring-inset",
            iconColor,
          )}
        >
          <Icon className="size-4" />
        </span>
        {connector && (
          <span className="border-secondary -mb-2 h-full w-px border-l border-dashed" />
        )}
      </div>
      <div className={cn("flex flex-1 flex-col gap-1", connector && "pb-5")}>
        {children}
      </div>
    </li>
  )
}

function TabEmptyState({
  title,
  description,
}: {
  title: string
  description: string
}) {
  return (
    <EmptyState size="sm">
      <EmptyState.Content>
        <EmptyState.Title>{title}</EmptyState.Title>
        <EmptyState.Description>{description}</EmptyState.Description>
      </EmptyState.Content>
    </EmptyState>
  )
}
